import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import type { TierName } from "@/app/hooks/useSubscription";

import { type Allowance, useFitCheckAllowances } from "../config/allowances";
import { type FitCheckImpact, useFitCheckImpact } from "../config/impact";

const TIER_LABEL: Record<TierName, string> = {
	BRONZE: "Free plan",
	SILVER: "Silver plan",
	GOLD: "Gold plan",
	PLATINUM: "Platinum plan",
};

function pct(part: number, whole: number): number {
	return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

// What FitCheck surfaced you already have (green) and the real gaps it caught (red).
function ValueGroups({ impact }: { impact: FitCheckImpact }) {
	const { fixable, realGaps, flaggedGaps, mustHavesMet, mustHavesAfter, mustHavesTotal } = impact;
	const onPaper = pct(mustHavesMet, mustHavesTotal);
	const after = pct(mustHavesAfter, mustHavesTotal);
	const growth = Math.max(0, mustHavesAfter - mustHavesMet);
	const remainingGap = Math.max(0, mustHavesTotal - mustHavesAfter);

	return (
		<>
			<div className="min-w-0 flex-1 px-5 py-[18px]">
				<p className="text-xs text-gray-600 font-semibold uppercase">FitCheck found</p>
				<p className="mt-2 text-4xl font-semibold tracking-tight text-green-600">{fixable}</p>
				<p className="mt-2 text-sm text-gray-600">strengths missing from your CV</p>
				{mustHavesTotal > 0 && (
					<div className="mt-3.5 flex h-2 gap-0.5 overflow-hidden rounded-full bg-gray-100">
						{/* solid = already on the CV · light = the growth FitCheck surfaced */}
						<span className="bg-green-600" style={{ flexGrow: mustHavesMet }} />
						<span className="bg-green-400" style={{ flexGrow: growth }} />
						<span style={{ flexGrow: remainingGap, background: "transparent" }} />
					</div>
				)}
				<p className="mt-2.5 text-xs text-gray-600">
					Enough to lift your match from <span className="text-gray-700">{onPaper}%</span> to{" "}
					<span className="font-semibold text-green-600">{after}%</span>
				</p>
			</div>

			<div className="min-w-0 flex-1 px-5 py-[18px]">
				<p className="text-xs text-gray-600 font-semibold uppercase">FitCheck caught</p>
				<p className="mt-2 text-4xl font-semibold tracking-tight text-red-500">{realGaps}</p>
				<p className="mt-2 text-sm text-gray-700">real gaps before you applied</p>
				<p className="mt-3.5 text-xs text-gray-600">Out of {flaggedGaps} gaps it checked across your roles</p>
			</div>
		</>
	);
}

function BalanceMeter({ label, allowance, className }: { label: string; allowance: Allowance; className?: string }) {
	const { unlimited, limit, remaining } = allowance;
	const width = unlimited ? 100 : limit > 0 ? (remaining / limit) * 100 : 0;
	return (
		<div className={className}>
			<div className="flex items-center justify-between text-sm text-gray-600">
				<span>{label}</span>
				{unlimited ? (
					<span className="text-gray-900">Unlimited</span>
				) : (
					<span>
						<span className="text-gray-900">{remaining}</span> / {limit}
					</span>
				)}
			</div>
			<div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-100">
				<div className="h-full rounded-full bg-gray-400" style={{ width: `${width}%` }} />
			</div>
		</div>
	);
}

function StatsSkeleton() {
	return <div className="mb-6 h-[168px] animate-pulse rounded-xl border border-gray-200 bg-white" />;
}

export function FitCheckStats() {
	const navigate = useNavigate();
	const { fitCheck, tailor, tier, isLoading: allowancesLoading } = useFitCheckAllowances();
	const impact = useFitCheckImpact();

	if (allowancesLoading || impact.isLoading) return <StatsSkeleton />;
	if (!impact.hasData) return null;

	return (
		<section className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white motion-safe:animate-fc-fade">
			<div className="flex flex-col divide-y divide-gray-200 sm:flex-row sm:divide-x sm:divide-y-0">
				{impact.hasAnswers ? (
					<ValueGroups impact={impact} />
				) : (
					<div className="min-w-0 flex-1 px-5 py-[18px]">
						<p className="text-xs text-gray-600 font-semibold uppercase">FitCheck insights</p>
						<p className="mt-2.5 text-sm text-gray-600">
							Answer the quick questions in any check to see the strengths FitCheck found and the real gaps it caught.
						</p>
					</div>
				)}

				<div className="min-w-0 flex-1 px-5 py-[18px]">
					<p className="text-xs text-gray-600 font-semibold uppercase">Left this month</p>
					<BalanceMeter label="FitChecks" allowance={fitCheck} className="mt-3.5" />
					<BalanceMeter label="Tailored resumes" allowance={tailor} className="mt-4" />
					<div className="mt-4 flex items-center gap-2 text-[13px] text-gray-600">
						<span>{TIER_LABEL[tier]}</span>
						{tier !== "PLATINUM" && (
							<>
								<span aria-hidden>·</span>
								<Button
									variant="link"
									size="sm"
									onClick={() => navigate("/payments")}
									className="h-auto p-0 text-primary-700"
								>
									Upgrade for more
									<ArrowRight className="ml-1 h-3.5 w-3.5" />
								</Button>
							</>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}

export default FitCheckStats;
